// mds-dojo — dijit/_WidgetBase — FAITHFUL port of references/dijit/_WidgetBase.js.
// Produced via codemod/transformLegacyJs (define→ESM+declare) then finished:
//  - declare from the faithful runtime (dojo/_base/declare-runtime)
//  - dojo-bidi support dropped (has("dojo-bidi")===false): _BidiMixin import +
//    the trailing `_WidgetBase.extend(_BidiMixin)` removed
//  - legacy `dijit-legacy-requires` ready() block + `require` import removed (no
//    AMD loader under Vite); `require.toUrl(blank.gif)` → literal (config.blankGif
//    is always set, so the fallback is never taken)
//  - fixed the transform's first-`return` mis-rewrite (getAttrs returns `ret`;
//    module `export default`s `_WidgetBase`)
// Method bodies are byte-faithful to dojo 1.x EXCEPT the two dual-compat shims that
// let this faithful base also serve the oracle app's native-class / mdsWidget world
// (marked "DUAL-COMPAT"):
//  1. _introspect walks the prototype chain via getOwnPropertyNames (native ES
//     class methods are NON-enumerable, so dojo's for-in would miss app widgets'
//     _setXxxAttr / onX). For declare-based (enumerable) protos it's identical.
//  2. Construction is driven either by dojo postscript→create (pure declare
//     widgets) OR by the mdsWidget construct-trap calling create() after the native
//     ctor chain (app widgets, flagged __mdsManaged). postscript defers when
//     managed; create() is idempotent.
import array from '../dojo/_base/array';
import aspect from '../dojo/aspect';
import config from '../dojo/_base/config';
import connect from '../dojo/_base/connect';
import declare from '../dojo/_base/declare-runtime';
import dom from '../dojo/dom';
import domAttr from '../dojo/dom-attr';
import domClass from '../dojo/dom-class';
import domConstruct from '../dojo/dom-construct';
import domGeometry from '../dojo/dom-geometry';
import domStyle from '../dojo/dom-style';
import kernel from '../dojo/_base/kernel';
import lang from '../dojo/_base/lang';
import on from '../dojo/on';
import Stateful from '../dojo/Stateful';
import topic from '../dojo/topic';
import win from '../dojo/_base/window';
import Destroyable from './Destroyable';
import registry from './registry';

void kernel; // dojo publishes legacy globals via kernel import side effect

const tagAttrs: any = {};

function getAttrs(obj: any): any {
  const ret: any = {};
  for (const attr in obj) {
    ret[attr.toLowerCase()] = true;
  }
  return ret;
}

function nonEmptyAttrToDom(attr: string): any {
  return function (this: any, val: any) {
    (domAttr as any)[val ? 'set' : 'remove'](this.domNode, attr, val);
    this._set(attr, val);
  };
}

function isEqual(a: any, b: any): boolean {
  return a === b || (a !== a && b !== b);
}

const _WidgetBase = declare('dijit._WidgetBase', [Stateful, Destroyable], {
  id: '',
  _setIdAttr: 'domNode', // copy to this.domNode even for auto-generated ids (→ domNode.id)

  lang: '',
  _setLangAttr: nonEmptyAttrToDom('lang'),

  dir: '',
  _setDirAttr: nonEmptyAttrToDom('dir'),

  'class': '',
  _setClassAttr: { node: 'domNode', type: 'class' }, // domClass.replace → MERGES

  _setTypeAttr: null,

  style: '',
  title: '',
  tooltip: '',
  baseClass: '',
  srcNodeRef: null,
  domNode: null,
  containerNode: null,

  ownerDocument: null,
  _setOwnerDocumentAttr: function (val: any) {
    this._set('ownerDocument', val);
  },

  attributeMap: {},

  _blankGif: config.blankGif || 'dojo/resources/blank.gif',

  textDir: '',

  //////////// INITIALIZATION METHODS ///////////////////////////////////////

  _introspect: function () {
    // Collect per-class metadata: _setterAttrs (attrs with custom setters) + _onMap.
    // DUAL-COMPAT: walk the prototype chain via getOwnPropertyNames so NON-enumerable
    // native-class methods (app widgets) are seen; declare protos (enumerable) work too.
    var ctor = this.constructor;
    if (ctor.hasOwnProperty('_setterAttrs')) { return; }
    var attrs = ctor._setterAttrs = [];
    var onMap = (ctor._onMap = {});
    var proto = ctor.prototype;
    var seen: any = {};

    for (var mapName in proto.attributeMap) {
      attrs.push(mapName);
    }

    var p = proto;
    while (p && p !== Object.prototype) {
      var names = Object.getOwnPropertyNames(p);
      for (var i = 0; i < names.length; i++) {
        var name = names[i];
        if (seen[name] || name === 'constructor') { continue; }
        seen[name] = true;
        if (/^on/.test(name)) {
          onMap[name.substring(2).toLowerCase()] = name;
        }
        if (/^_set[A-Z](.*)Attr$/.test(name)) {
          var attrName = name.charAt(4).toLowerCase() + name.substr(5, name.length - 9);
          if (!proto.attributeMap || !(attrName in proto.attributeMap)) {
            attrs.push(attrName);
          }
        }
      }
      p = Object.getPrototypeOf(p);
    }
  },

  // DUAL-COMPAT: the mdsWidget construct-trap calls __create() after the native
  // ctor chain. Alias to create() so both worlds share one pipeline.
  __create: function (params: any, srcNodeRef: any) {
    return this.create(params, srcNodeRef);
  },

  postscript: function (params: any, srcNodeRef: any) {
    // DUAL-COMPAT: app widgets (native subclasses wrapped by mdsWidget) defer the
    // lifecycle to the construct-trap so it runs AFTER their native field
    // initializers. Pure declare widgets run it here (dojo behavior).
    if (this.__mdsManaged) { return; }
    this.create(params, srcNodeRef);
  },

  create: function (params: any, srcNodeRef: any) {
    if (this._created) { return; } // idempotent (dual-compat guard)

    this._introspect();

    this.srcNodeRef = dom.byId(srcNodeRef);

    this._connects = [];
    this._supportingWidgets = [];

    if (this.srcNodeRef && this.srcNodeRef.id && (typeof this.srcNodeRef.id == 'string')) {
      this.id = this.srcNodeRef.id;
    }

    if (params) {
      this.params = params;
      lang.mixin(this, params);
    }
    this.postMixInProperties();

    if (!this.id) {
      this.id = registry.getUniqueId(this.declaredClass.replace(/\./g, '_'));
      if (this.params) {
        delete this.params.id;
      }
    }

    this.ownerDocument = this.ownerDocument || (this.srcNodeRef ? this.srcNodeRef.ownerDocument : document);
    this.ownerDocumentBody = win.body(this.ownerDocument);

    registry.add(this);

    this.buildRendering();

    var deleteSrcNodeRef;

    if (this.domNode) {
      this._applyAttributes();

      var source = this.srcNodeRef;
      if (source && source.parentNode && this.domNode !== source) {
        source.parentNode.replaceChild(this.domNode, source);
        deleteSrcNodeRef = true;
      }

      this.domNode.setAttribute('widgetId', this.id);
    }
    this.postCreate();

    if (deleteSrcNodeRef) {
      delete this.srcNodeRef;
    }

    this._created = true;
  },

  _applyAttributes: function () {
    var params: any = {};
    for (var key in this.params || {}) {
      params[key] = this._get(key);
    }

    array.forEach(this.constructor._setterAttrs, function (this: any, k: string) {
      if (!(k in params)) {
        var val = this._get(k);
        if (val) {
          this.set(k, val);
        }
      }
    }, this);

    for (key in params) {
      this.set(key, params[key]);
    }
  },

  postMixInProperties: function () { },

  buildRendering: function () {
    if (!this.domNode) {
      this.domNode = this.srcNodeRef || this.ownerDocument.createElement('div');
    }
    if (this.baseClass) {
      var classes = this.baseClass.split(' ');
      if (!this.isLeftToRight()) {
        classes = classes.concat(array.map(classes, function (name: string) {
          return name + 'Rtl';
        }));
      }
      domClass.add(this.domNode, classes);
    }
  },

  postCreate: function () { },

  startup: function () {
    if (this._started) {
      return;
    }
    this._started = true;
    array.forEach(this.getChildren(), function (obj: any) {
      if (!obj._started && !obj._destroyed && lang.isFunction(obj.startup)) {
        obj.startup();
        obj._started = true;
      }
    });
  },

  //////////// DESTROY FUNCTIONS ////////////////////////////////

  destroyRecursive: function (preserveDom: boolean) {
    this._beingDestroyed = true;
    this.destroyDescendants(preserveDom);
    this.destroy(preserveDom);
  },

  destroy: function (preserveDom: boolean) {
    this._beingDestroyed = true;
    this.uninitialize();

    function destroy(w: any) {
      if (w.destroyRecursive) {
        w.destroyRecursive(preserveDom);
      } else if (w.destroy) {
        w.destroy(preserveDom);
      }
    }

    array.forEach(this._connects || [], lang.hitch(this, 'disconnect'));
    array.forEach(this._supportingWidgets || [], destroy);

    if (this.domNode) {
      array.forEach(registry.findWidgets(this.domNode, this.containerNode), destroy);
    }

    this.destroyRendering(preserveDom);
    registry.remove(this.id);
    this._destroyed = true;
  },

  destroyRendering: function (preserveDom: boolean) {
    if (this.bgIframe) {
      this.bgIframe.destroy(preserveDom);
      delete this.bgIframe;
    }

    if (this.domNode) {
      if (preserveDom) {
        domAttr.remove(this.domNode, 'widgetId');
      } else {
        domConstruct.destroy(this.domNode);
      }
      delete this.domNode;
    }

    if (this.srcNodeRef) {
      if (!preserveDom) {
        domConstruct.destroy(this.srcNodeRef);
      }
      delete this.srcNodeRef;
    }
  },

  destroyDescendants: function (preserveDom: boolean) {
    array.forEach(this.getChildren(), function (widget: any) {
      if (widget.destroyRecursive) {
        widget.destroyRecursive(preserveDom);
      }
    });
  },

  uninitialize: function () {
    return false;
  },

  ////////////////// GET/SET, CUSTOM SETTERS, ETC. ///////////////////

  _setStyleAttr: function (value: any) {
    var mapNode = this.domNode;
    if (lang.isObject(value)) {
      domStyle.set(mapNode, value);
    } else {
      if (mapNode.style.cssText) {
        mapNode.style.cssText += '; ' + value;
      } else {
        mapNode.style.cssText = value;
      }
    }
    this._set('style', value);
  },

  _attrToDom: function (attr: string, value: any, commands: any) {
    commands = arguments.length >= 3 ? commands : this.attributeMap[attr];

    array.forEach(lang.isArray(commands) ? commands : [commands], function (this: any, command: any) {
      var mapNode = this[command.node || command || 'domNode'];
      var type = command.type || 'attribute';

      switch (type) {
        case 'attribute':
          if (lang.isFunction(value)) {
            value = lang.hitch(this, value);
          }
          var attrName = command.attribute ? command.attribute
            : (/^on[A-Z][a-zA-Z]*$/.test(attr) ? attr.toLowerCase() : attr);

          if (mapNode.tagName) {
            domAttr.set(mapNode, attrName, value);
          } else {
            mapNode.set(attrName, value);
          }
          break;
        case 'innerText':
          mapNode.innerHTML = '';
          mapNode.appendChild(this.ownerDocument.createTextNode(value));
          break;
        case 'textContent':
          mapNode.textContent = value;
          break;
        case 'innerHTML':
          mapNode.innerHTML = value;
          break;
        case 'class':
          domClass.replace(mapNode, value, this[attr]);
          break;
        case 'toggleClass':
          domClass.toggle(mapNode, command.className || attr, value);
          break;
      }
    }, this);
  },

  get: function (name: string) {
    var names = this._getAttrNames(name);
    return this[names.g] ? this[names.g]() : this._get(name);
  },

  set: function (name: any, value: any) {
    if (typeof name === 'object') {
      for (var x in name) {
        this.set(x, name[x]);
      }
      return this;
    }
    var names = this._getAttrNames(name),
      setter = this[names.s];
    var result: any;
    if (lang.isFunction(setter)) {
      result = setter.apply(this, Array.prototype.slice.call(arguments, 1));
    } else {
      var defaultNode = this.focusNode && !lang.isFunction(this.focusNode) ? 'focusNode' : 'domNode',
        tag = this[defaultNode] && this[defaultNode].tagName,
        attrsForTag = tag && (tagAttrs[tag] || (tagAttrs[tag] = getAttrs(this[defaultNode]))),
        map = name in this.attributeMap ? this.attributeMap[name]
          : names.s in this ? this[names.s]
            : ((attrsForTag && names.l in attrsForTag && typeof value != 'function')
              || /^aria-|^data-|^role$/.test(name)) ? defaultNode : null;
      if (map != null) {
        this._attrToDom(name, value, map);
      }
      this._set(name, value);
    }
    return result || this;
  },

  _attrPairNames: {},
  _getAttrNames: function (name: string) {
    var apn = this._attrPairNames;
    if (apn[name]) {
      return apn[name];
    }
    var uc = name.replace(/^[a-z]|-[a-zA-Z]/g, function (c: string) {
      return c.charAt(c.length - 1).toUpperCase();
    });
    return (apn[name] = {
      n: name + 'Node',
      s: '_set' + uc + 'Attr',
      g: '_get' + uc + 'Attr',
      l: uc.toLowerCase(),
    });
  },

  _set: function (name: string, value: any) {
    var oldValue = this[name];
    this[name] = value;
    if (this._created && !isEqual(oldValue, value)) {
      if (this._watchCallbacks) {
        this._watchCallbacks(name, oldValue, value);
      }
      this.emit('attrmodified-' + name, {
        detail: {
          prevValue: oldValue,
          newValue: value,
        },
      });
    }
  },

  _get: function (name: string) {
    return this[name];
  },

  emit: function (type: string, eventObj: any, callbackArgs: any) {
    eventObj = eventObj || {};
    if (eventObj.bubbles === undefined) {
      eventObj.bubbles = true;
    }
    if (eventObj.cancelable === undefined) {
      eventObj.cancelable = true;
    }
    if (!eventObj.detail) {
      eventObj.detail = {};
    }
    eventObj.detail.widget = this;

    var ret, callback = this['on' + type];
    if (callback) {
      ret = callback.apply(this, callbackArgs ? callbackArgs : [eventObj]);
    }

    if (this._started && !this._beingDestroyed && this.domNode) {
      (on as any).emit(this.domNode, type.toLowerCase(), eventObj);
    }

    return ret;
  },

  on: function (type: any, func: any) {
    var widgetMethod = this._onMap(type);
    if (widgetMethod) {
      return aspect.after(this, widgetMethod, func, true);
    }
    return this.own(on(this.domNode, type, func))[0];
  },

  _onMap: function (type: any) {
    var ctor = this.constructor, map = ctor._onMap;
    if (!map) {
      map = (ctor._onMap = {});
      // DUAL-COMPAT: getOwnPropertyNames walk (non-enumerable native methods).
      var p = ctor.prototype;
      var seen: any = {};
      while (p && p !== Object.prototype) {
        var names = Object.getOwnPropertyNames(p);
        for (var i = 0; i < names.length; i++) {
          var attr = names[i];
          if (seen[attr]) { continue; }
          seen[attr] = true;
          if (/^on/.test(attr)) {
            map[attr.replace(/^on/, '').toLowerCase()] = attr;
          }
        }
        p = Object.getPrototypeOf(p);
      }
    }
    return map[typeof type == 'string' && type.toLowerCase()];
  },

  toString: function () {
    return '[Widget ' + this.declaredClass + ', ' + (this.id || 'NO ID') + ']';
  },

  getChildren: function () {
    return this.containerNode ? registry.findWidgets(this.containerNode) : [];
  },

  getParent: function () {
    return registry.getEnclosingWidget(this.domNode.parentNode);
  },

  connect: function (obj: any, event: any, method: any) {
    return this.own(connect.connect(obj, event, this, method))[0];
  },

  disconnect: function (handle: any) {
    handle.remove();
  },

  subscribe: function (t: string, method: any) {
    return this.own(topic.subscribe(t, lang.hitch(this, method)))[0];
  },

  unsubscribe: function (handle: any) {
    handle.remove();
  },

  isLeftToRight: function () {
    return this.dir ? (this.dir.toLowerCase() == 'ltr') : domGeometry.isBodyLtr(this.ownerDocument);
  },

  isFocusable: function () {
    return this.focus && (domStyle.get(this.domNode, 'display') != 'none');
  },

  placeAt: function (reference: any, position?: any) {
    var refWidget = !reference.tagName && registry.byId(reference);
    if (refWidget && refWidget.addChild && (!position || typeof position === 'number')) {
      refWidget.addChild(this, position);
    } else {
      var ref = refWidget && ('domNode' in refWidget)
        ? (refWidget.containerNode && !/after|before|replace/.test(position || '')
          ? refWidget.containerNode : refWidget.domNode)
        : dom.byId(reference, this.ownerDocument);
      domConstruct.place(this.domNode, ref, position);

      if (!this._started && (this.getParent() || {})._started) {
        this.startup();
      }
    }
    return this;
  },

  defer: function (fcn: any, delay: number) {
    var timer: any = setTimeout(lang.hitch(this,
      function (this: any) {
        if (!timer) {
          return;
        }
        timer = null;
        if (!this._destroyed) {
          lang.hitch(this, fcn)();
        }
      }),
      delay || 0,
    );
    return {
      remove: function () {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        return null;
      },
    };
  },
});

export default _WidgetBase;
